import type { Metadata } from "next"

import type { DishAddOnCategoryType, OrderStatus, UserType, VendorType } from "@/lib/zenstack/models"

const LOCALES = ["en", "fr"] as const

const BUCKET_STORAGE = {
  MERCHANT_BUCKET: {
    name: "merchant",
    folders: {
      publicBanner: "public/banner",
      dishes: "public/dishes",
    },
    maxBanners: 30,
    getBannerFolder: (userId: string) => `${BUCKET_STORAGE.MERCHANT_BUCKET.folders.publicBanner}/${userId}`,
    getBannerPath: (userId: string, bannerName: string) =>
      `${BUCKET_STORAGE.MERCHANT_BUCKET.getBannerFolder(userId)}/${bannerName}`,
    getDishesFolder: (userId: string) => `${BUCKET_STORAGE.MERCHANT_BUCKET.folders.dishes}/${userId}`,
    getDishesPath: (userId: string, dishName: string) =>
      `${BUCKET_STORAGE.MERCHANT_BUCKET.getDishesFolder(userId)}/${dishName}`,
  } as const,
}

/**
 * User types
 */
const CUSTOMER: UserType = "CUSTOMER"
const MERCHANT: UserType = "MERCHANT"
const MERCHANT_COUPON_REDEEMER: UserType = "MERCHANT_COUPON_REDEEMER"
const USER_TYPE = {
  CUSTOMER,
  MERCHANT,
  MERCHANT_COUPON_REDEEMER,
} as const
export type TUSER_TYPE = (typeof USER_TYPE)[keyof typeof USER_TYPE]

const BANNER_WIDTH = 800
const BANNER_HEIGHT = 500
export type TBANNER_WIDTH = typeof BANNER_WIDTH
export type TBANNER_HEIGHT = typeof BANNER_HEIGHT

///
/// Open Graph
///

const MASTER_DOMAIN = "https://www.spectra.discount"
const title = "Spectra Discount"
const description =
  "Join me on Spectra to unlock exclusive discounts for everyone 35 and under! The more people join, the more discounts we all unlock! Sign up now"

const OG_METADATA: Metadata = {
  metadataBase: new URL(MASTER_DOMAIN),
  title,
  description,
  openGraph: {
    type: "website",
    title,
    description,
    url: MASTER_DOMAIN,
    countryName: "Canada",
    emails: ["spectratechcorp@gmail.com"],
    locale: "en_CA",
    images: [
      {
        url: `${MASTER_DOMAIN}/logo.png`,
        width: 1200,
        height: 630,
        alt: "Spectra Discount",
        type: "image/png",
        secureUrl: `${MASTER_DOMAIN}/logo.png`,
      },
    ],
    siteName: "Spectra Discount",
  },
  twitter: {
    card: "summary_large_image",
    title,
    description,
    images: [`${MASTER_DOMAIN}/logo.png`],
  },
}

///
/// Socials
///

const SOCIALS = {
  facebook: "https://www.facebook.com/profile.php?id=61571077800249",
  instagram: "https://www.instagram.com/spectra.discount",
  x: "https://x.com/spectratc",
  tiktok: "https://www.tiktok.com/@spectra.discount?_t=ZM-8wT0t5K2wX1&_r=1",
}

// Merchant
const MERCHANT_DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const
const MERCHANT_DAYS_FR = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"] as const

const ORDER_STATUS_PENDING: OrderStatus[] = ["PENDING"] as const
const ORDER_STATUS_ONGOING: OrderStatus[] = ["PENDING", "MERCHANT_ACCEPTED"] as const
const ORDER_STATUS_FAILED: OrderStatus[] = ["MERCHANT_REJECTED", "MERCHANT_NOTIFICATION_EXPIRED"] as const
const ORDER_STATUS_SUCCESS: OrderStatus[] = ["FULFILLED"] as const
const ORDER_STATUS_FLOW_FINISHED: OrderStatus[] = [...ORDER_STATUS_FAILED, ...ORDER_STATUS_SUCCESS] as const

const MAX_TAGS_PER_MERCHANT_PER_LANGUAGE = 10
///
/// Local Storage
///

const LOCAL_STORAGE_KEYS = {
  CART: () => `user-cart`,
} as const

// Other

const GST_RATE = 0.05 // 5%
const HST_RATE = 0.09975 // 9.975%
const MINIMUM_TIP_RATE = 0 // 0%
const MAXIMUM_TIP_RATE = 1 // 100%
const APPLICATION_FEE_RATE = 0.05 // 5%
const STRIPE_FEE_RATE = 0.029 // 2.9%
const STRIPE_FEE_RATE_CUSTOMER = 0.015 // 1.5%
const STRIPE_FEE_BASE_AMOUNT_CENTS = 30 // $0.30
const STRIPE_FEE_BASE_AMOUNT_CENTS_CUSTOMER = 15 // $0.15

const ESTIMATED_SAVING_MULTIPLIER = 1.3225

const PAYMENT_INTENT_DEFAULT_AMOUNT = 50 // $0.50 Minimum for Stripe
const MIN_ORDER_AMOUNT_CENTS = 10_00 // $10.00

const COUPON_VALIDITY_PERIOD = 30 * 24 * 60 * 60 * 1000 // 30 days

const MIN_AGE = 18
const MAX_AGE = 35
const START_BIRTHDATE = new Date("1990-01-01")

const MIN_DISCOUNT = 20
const MAX_DISCOUNT = 100

const CLOSED_TIMES = "00:00-00:00"

const RATE_LIMIT = {
  MAX_PENDING_CHECKOUT_SESSIONS_PER_USER: 5,
}

const STRIPE_DASHBOARD_URL = "https://dashboard.stripe.com/dashboard"

const DINING_REQUEST_TIMEOUT_MS = 30 * 1000 * 60 // 30 minutes

const JOB_WORKER_KEYS = {
  EXPIRED_ORDER_QUEUE: "EXPIRED_ORDER_QUEUE",
} as const

const ADD_ON_TYPES: [
  Extract<DishAddOnCategoryType, "ONE_OF">,
  Extract<DishAddOnCategoryType, "MULTIPLE">,
  Extract<DishAddOnCategoryType, "COUNTABLE">,
] = ["ONE_OF", "MULTIPLE", "COUNTABLE"]

const VENDOR_TYPES: [Extract<VendorType, "RESTAURANT">, Extract<VendorType, "BEAUTY">] = ["RESTAURANT", "BEAUTY"]

/// ENV getters

const ENV_STRIPE_PUBLISHABLE_KEY = () => {
  const key = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
  if (!key) {
    throw new Error("STRIPE_PUBLISHABLE_KEY is not set")
  }
  return key
}

export const CONST = {
  ADD_ON_TYPES,
  BUCKET_STORAGE,
  ESTIMATED_SAVING_MULTIPLIER,
  COUPON_VALIDITY_PERIOD,
  MAX_TAGS_PER_MERCHANT_PER_LANGUAGE,
  LOCALES,
  ORDER_STATUS_PENDING,
  ORDER_STATUS_ONGOING,
  ORDER_STATUS_FAILED,
  ORDER_STATUS_SUCCESS,
  ORDER_STATUS_FLOW_FINISHED,
  USER_TYPE,
  CUSTOMER,
  MERCHANT,
  BANNER_WIDTH,
  BANNER_HEIGHT,
  OG_METADATA,
  SOCIALS,
  MERCHANT_DAYS,
  MERCHANT_DAYS_FR,
  LOCAL_STORAGE_KEYS,
  MIN_AGE,
  MAX_AGE,
  RATE_LIMIT,
  ENV_STRIPE_PUBLISHABLE_KEY,
  MASTER_DOMAIN,
  STRIPE_DASHBOARD_URL,
  DINING_REQUEST_TIMEOUT_MS,
  PAYMENT_INTENT_DEFAULT_AMOUNT,
  GST_RATE,
  HST_RATE,
  MINIMUM_TIP_RATE,
  MAXIMUM_TIP_RATE,
  APPLICATION_FEE_RATE,
  JOB_WORKER_KEYS,
  STRIPE_FEE_RATE,
  STRIPE_FEE_BASE_AMOUNT_CENTS,
  MIN_ORDER_AMOUNT_CENTS,
  STRIPE_FEE_RATE_CUSTOMER,
  STRIPE_FEE_BASE_AMOUNT_CENTS_CUSTOMER,
  MIN_DISCOUNT,
  MAX_DISCOUNT,
  START_BIRTHDATE,
  CLOSED_TIMES,
  VENDOR_TYPES,
} as const
